import { lazy, type FC } from 'react';
import type { PrimerInitData } from '../paymentTypes';
import { getPrimerRefusalMessage, readPrimerPendingPayment } from './primerHelpers';
import type { CheckoutFormProps, CheckoutProviderAdapter } from './types';

// Primer Checkout (primer-js) on the flat method list (primer-payment-page-redesign.md §4): the page
// draws the rows and the pay button, the form fills them. Repay links keep Universal Checkout
// (RepayCheckoutState).
const loadFlatListForm = () => import('../CheckoutForms/PrimerJsFlatListForm');
const FlatListForm = lazy(loadFlatListForm);

const PrimerForm: FC<CheckoutFormProps<PrimerInitData>> = ({ initData, hideBillingCountry, hideExpressCheckout, ...props }) => (
  <FlatListForm primerInitData={initData} {...props} />
);

export const primerAdapter: CheckoutProviderAdapter<PrimerInitData> = {
  code: 'primer',

  buildInitData: (dto, { isSubscription }) => ({
    clientToken: dto.clientToken ?? dto.gatewayResult?.clientToken,
    environment: dto.environment,
    currency: dto.currency,
    paymentMethods: dto.gatewayResult?.paymentMethods ?? [],
    isSubscription,
    customerData: dto.customerData,
    orderAddresses: dto.orderAddresses,
    hostPaymentTypes: dto.hostPaymentTypes ?? [],
  }),

  applyAllowList: (init, native) => ({ ...init, paymentMethods: init.paymentMethods.filter((method) => native.includes(method)) }),

  loadSdk: loadFlatListForm,
  Form: PrimerForm,
  usesFlatList: true,

  // Primer also reports a cancelled payment (e.g. a closed PayPal popup): treated as refused.
  interpretResult: (result, language) => ({
    ...(result.resultCode === 'Cancelled' && { outcomeHint: 'refused' as const }),
    message: getPrimerRefusalMessage(language, result.primerError),
  }),

  interpretError: (errorResponse, language) => {
    const primerError = errorResponse?.primerError;
    if (!primerError) return undefined;
    return { message: getPrimerRefusalMessage(language, primerError), reason: `primer: ${primerError.code || 'unknown'}` };
  },

  // A redirect method saved the payment id (make_payment's approval) before leaving the page.
  readRedirectReturn: (_search, hashID) => ({ provider: 'primer', primerPaymentId: readPrimerPendingPayment(hashID) }),

  contextOverrides: () => ({ paymentServiceProvider: 'primer', checkoutProvider: 'primer' }),
};
